import {gql} from '@apollo/client';
import React from 'react';

import {QueryResultType} from 'graphql-client/BenchlingQuery';

import {HOMEPAGE_SECTION_TEST_COMPONENT} from '../HomepageConstants';
import type {
  HomepageWorksheetsQuery,
  HomepageWorksheetsQueryVariables,
  HomepageWorksheetsQuery_worksheets_WorksheetNode as WorksheetNode,
} from './__generated/HomepageWorksheetsQuery';
import {reviewableRowProps} from './MyWorkRowHelpers';
import {MyWorkRowsList} from './MyWorkRowsList';
import {useMyWorkRows} from './useMyWorkRows';

const TEST_COMPONENT = HOMEPAGE_SECTION_TEST_COMPONENT.MY_WORK;

export const HOMEPAGE_WORKSHEETS_QUERY = gql`
  query HomepageWorksheetsQuery($worksheetIds: [StrictString!]!) @skipAudit @engComponent(name: NAVIGATION) {
    worksheets(ids: $worksheetIds) {
      ... on WorksheetNode {
        id
        name
        modifiedAt
        review {
          id
          status
        }
        editUrl
      }
      ... on InaccessibleInfoNode {
        invalidId
        canRequestAccess
      }
    }
  }
`;

/** Fetches and lists worksheets for the selected review-status filter. */
export default function WorksheetsRows({
  selectedWorksheetIds,
}: {
  selectedWorksheetIds: Array<string> | null;
}): JSX.Element {
  const rowsResult = useMyWorkRows<HomepageWorksheetsQuery, HomepageWorksheetsQueryVariables, WorksheetNode>({
    query: HOMEPAGE_WORKSHEETS_QUERY,
    variables: {worksheetIds: selectedWorksheetIds!},
    skip: selectedWorksheetIds == null,
    getItems: (data) => data.worksheets,
    toRowProps: (item) => reviewableRowProps(item),
    errorMessage: 'Error loading worksheets',
    testComponent: TEST_COMPONENT,
  });

  if (rowsResult.type !== QueryResultType.SUCCESS) {
    return rowsResult.element;
  }

  return (
    <MyWorkRowsList
      rows={rowsResult.rows}
      emptyMessage="No worksheets with selected statuses"
      emptyMessageDataTestAttrs={{
        component: TEST_COMPONENT,
        element: 'messageRow',
        key: 'emptyWorksheets',
      }}
    />
  );
}
